import { useCallback, useEffect, useRef, useState } from "react";

import Config from "~/config";
import isSwOutboundMessage, { SW_MESSAGE_TYPE } from "~/helpers/sw/messageContract";
import { useAppDispatch } from "~/hooks/reduxHooks";
import NetworkStatusContext from "~/providers/NetworkStatusContext";
import { baseApi } from "~/services/api";
import {
	CYCLE_COUNT_MUTATION_NAME,
	cycleCountReconcileRequested,
	operationEnqueued,
	operationFailed,
	operationPosted,
	queueReplayComplete,
	queueReplayStarted,
	queueSnapshotReceived
} from "~/store/offlineQueueSlice";

interface NetworkStatusProviderProps {
	children: React.ReactNode;
}

const ONLINE_INTERVAL = 30_000;
const OFFLINE_INTERVAL = 5_000;
const PING_TIMEOUT = 5_000;
const FAILURE_THRESHOLD = 2;

/**
 * Tracks connectivity and bridges the service worker's offline-queue broadcasts into Redux.
 *
 * Connectivity uses the browser online/offline events as the fast signal plus an adaptive ping to
 * the GraphQL origin's `/healthz` (polled faster while offline) so a dead-but-"online" connection
 * is still detected. The SW message bridge mirrors the durable Background-Sync queue into
 * offlineQueueSlice so the shell can surface queued/posting/posted/failed work, and — for cycle-count
 * replays specifically — invalidates the `CycleCountData` RTK Query tag once a replay resolves, so the
 * mission task list / lot-count views reconcile to server truth instead of a generic
 * `refetchOnReconnect`, which would race the SW's OWN replay and could refetch before it lands.
 */
const NetworkStatusProvider = ({ children }: NetworkStatusProviderProps) => {
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const failureCount = useRef(0);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const dispatch = useAppDispatch();

	const checkConnectivity = useCallback(async () => {
		try {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT);
			const baseUrl = new URL(Config.endpoint).origin;
			await fetch(`${baseUrl}/healthz`, { method: "GET", signal: controller.signal });
			clearTimeout(timeout);
			failureCount.current = 0;
			setIsOnline(true);
		} catch {
			failureCount.current += 1;
			if (failureCount.current >= FAILURE_THRESHOLD) {
				setIsOnline(false);
			}
		}
	}, []);

	useEffect(() => {
		const handleOnline = () => {
			failureCount.current = 0;
			setIsOnline(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		if (intervalRef.current) clearInterval(intervalRef.current);
		const interval = isOnline ? ONLINE_INTERVAL : OFFLINE_INTERVAL;
		intervalRef.current = setInterval(checkConnectivity, interval);
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [isOnline, checkConnectivity]);

	useEffect(() => {
		if (!("serviceWorker" in navigator)) return;

		/**
		 * Reconcile-on-reconnect (Atlas: offline read cache): once the service worker confirms a
		 * queued `AddBpfLotChange` replay resolved — success OR a definitive business failure, either
		 * way the server now has the real answer — reconcile every cycle-count read cache so it
		 * converges on server truth instead of trusting whatever was last in memory. A still-`transient`
		 * replay (will retry) never reaches this — only QUEUE_REPLAY_SUCCESS/QUEUE_REPLAY_FAILED do.
		 *
		 * Two DELIBERATELY DIFFERENT mechanisms, dispatched together:
		 *  - `invalidateTags(["CycleCountData"])` covers the mission list and lot-counts — neither ever
		 *    auto-pages, so their cached `arg.after` is always `undefined` and the tag's own refetch is
		 *    always a safe page-1 request.
		 *  - `cycleCountReconcileRequested()` covers the mission TASK list — `useCycleCountMissionTasks`
		 *    watches it to reset its own auto-paging cursor back to page 1. It is NOT tag-driven: RTK
		 *    Query allows only one in-flight request per cache entry, and `invalidateTags`' own refetch
		 *    dispatches synchronously (before this hook's render can reset its cursor) using whatever
		 *    page the auto-pager last stored — always winning that race and silently starving a
		 *    tag-based reset forever for a mission running past `PAGE_SIZE` (see `missionApi.ts`'s top
		 *    docstring — P1 review finding, a 101-task mission's progress banner staying frozen at its
		 *    pre-reconnect count).
		 */
		const reconcileCycleCountDataIfRelevant = (mutationName: string) => {
			if (mutationName !== CYCLE_COUNT_MUTATION_NAME) return;
			dispatch(cycleCountReconcileRequested());
			dispatch(baseApi.util.invalidateTags(["CycleCountData"]));
		};

		const handleMessage = (event: MessageEvent) => {
			if (!isSwOutboundMessage(event.data)) return;
			const message = event.data;
			switch (message.type) {
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED:
					dispatch(queueReplayStarted());
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE:
					dispatch(queueReplayComplete());
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_ENQUEUED:
					dispatch(
						operationEnqueued({
							clientId: message.clientId,
							missionEntryId: message.missionEntryId,
							mutationName: message.mutationName
						})
					);
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS:
					dispatch(
						operationPosted({
							clientId: message.clientId,
							missionEntryId: message.missionEntryId,
							mutationName: message.mutationName
						})
					);
					reconcileCycleCountDataIfRelevant(message.mutationName);
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED:
					dispatch(
						operationFailed({
							clientId: message.clientId,
							missionEntryId: message.missionEntryId,
							mutationName: message.mutationName,
							reason: message.reason
						})
					);
					reconcileCycleCountDataIfRelevant(message.mutationName);
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT:
					dispatch(queueSnapshotReceived(message.entries));
					return;
			}
		};
		// Captured once, rather than re-reading `navigator.serviceWorker` in the cleanup below — the
		// container this effect subscribed to is the one it must unsubscribe from, even if something
		// swaps or tears down `navigator.serviceWorker` before unmount (a fake one in tests, notably).
		const container = navigator.serviceWorker;
		container.addEventListener("message", handleMessage);

		container.ready
			.then((registration) => {
				registration.active?.postMessage({ type: SW_MESSAGE_TYPE.REQUEST_QUEUE_STATE });
			})
			.catch(() => {
				// SW not installed yet (first visit before install, or dev without PWA).
			});

		return () => {
			container.removeEventListener("message", handleMessage);
		};
	}, [dispatch]);

	return <NetworkStatusContext.Provider value={{ isOnline }}>{children}</NetworkStatusContext.Provider>;
};

export default NetworkStatusProvider;
